/**
 * Utility functions for refinance calculations
 */

/**
 * Calculates loan payoff with dynamic minimum payments that change as balance decreases
 * This is more accurate for accounts using percentage-based minimum payments
 * @param {number} balance - Current loan balance
 * @param {number} apr - Annual percentage rate (as decimal, e.g., 0.1999)
 * @param {number} minimumPaymentPercentage - Minimum payment as percentage of balance (e.g., 0.025 for 2.5%)
 * @param {number} minimumPaymentFloor - Minimum payment floor amount (e.g., 25)
 * @returns {Object} - Contains monthsToPayoff, totalPayments, totalInterest, finalBalance
 */
export const calculateLoanPayoffWithDynamicMinimum = (balance, apr, minimumPaymentPercentage, minimumPaymentFloor) => {
    // Input validation
    if (!balance || balance <= 0) {
        return {
            monthsToPayoff: 0,
            totalPayments: 0,
            totalInterest: 0,
            finalBalance: 0,
            isValid: false
        };
    }

    if (!minimumPaymentPercentage || minimumPaymentPercentage <= 0) {
        return {
            monthsToPayoff: 0,
            totalPayments: 0,
            totalInterest: 0,
            finalBalance: balance,
            isValid: false
        };
    }

    // Convert APR to monthly rate (APR is already in decimal format 0-1)
    const monthlyRate = apr > 0 ? apr / 12 : 0;
    
    let remainingBalance = balance;
    let totalPaymentsMade = 0;
    let totalInterestPaid = 0;
    let months = 0;
    const maxMonths = 600; // Safety limit (50 years)

    // Calculate month by month with dynamic minimum payments
    while (remainingBalance > 0.01 && months < maxMonths) {
        const interestPayment = remainingBalance * monthlyRate;
        
        // Calculate minimum payment for this month
        // Minimum payment is typically: max(percentage of balance, interest + 1% of principal, floor amount)
        const percentageBasedPayment = remainingBalance * minimumPaymentPercentage;
        const interestPlus1Percent = interestPayment + (remainingBalance * 0.01); // Interest + 1% of balance
        const minimumPayment = Math.max(percentageBasedPayment, interestPlus1Percent, minimumPaymentFloor || 0);
        
        // If the remaining balance is very small, just pay it off
        const actualPayment = Math.min(minimumPayment, remainingBalance + interestPayment);
        
        const principalPayment = actualPayment - interestPayment;
        
        // Check if payment can cover interest
        if (principalPayment <= 0) {
            // Payment too low to cover interest - loan will never be paid off
            return {
                monthsToPayoff: Infinity,
                totalPayments: Infinity,
                totalInterest: Infinity,
                finalBalance: remainingBalance,
                isValid: false,
                error: 'Minimum payment too low to cover interest'
            };
        }

        totalPaymentsMade += actualPayment;
        totalInterestPaid += interestPayment;
        remainingBalance -= principalPayment;
        months++;
    }

    return {
        monthsToPayoff: months,
        totalPayments: totalPaymentsMade,
        totalInterest: totalInterestPaid,
        finalBalance: Math.max(0, remainingBalance),
        isValid: true
    };
};

/**
 * Calculates loan payoff information given balance, APR, and monthly payment
 * @param {number} balance - Current loan balance
 * @param {number} apr - Annual percentage rate (as decimal, e.g., 0.1999)
 * @param {number} monthlyPayment - Monthly payment amount
 * @returns {Object} - Contains monthsToPayoff, totalPayments, totalInterest, finalBalance
 */
export const calculateLoanPayoff = (balance, apr, monthlyPayment) => {
    // Input validation
    if (!balance || balance <= 0) {
        return {
            monthsToPayoff: 0,
            totalPayments: 0,
            totalInterest: 0,
            finalBalance: 0,
            isValid: false
        };
    }

    if (!monthlyPayment || monthlyPayment <= 0) {
        return {
            monthsToPayoff: 0,
            totalPayments: 0,
            totalInterest: 0,
            finalBalance: balance,
            isValid: false
        };
    }

    // Convert APR to monthly rate (APR is already in decimal format 0-1)
    const monthlyRate = apr > 0 ? apr / 12 : 0;
    
    let remainingBalance = balance;
    let totalInterestPaid = 0;
    let months = 0;
    const maxMonths = 600; // Safety limit (50 years)

    // If no interest, simple calculation
    if (monthlyRate === 0) {
        const monthsToPayoff = Math.ceil(balance / monthlyPayment);
        return {
            monthsToPayoff,
            totalPayments: monthsToPayoff * monthlyPayment,
            totalInterest: 0,
            finalBalance: 0,
            isValid: true
        };
    }

    // Calculate month by month
    while (remainingBalance > 0.01 && months < maxMonths) {
        const interestPayment = remainingBalance * monthlyRate;
        const principalPayment = Math.min(monthlyPayment - interestPayment, remainingBalance);
        
        // Check if payment can cover interest
        if (principalPayment <= 0) {
            // Payment too low to cover interest - loan will never be paid off
            return {
                monthsToPayoff: Infinity,
                totalPayments: Infinity,
                totalInterest: Infinity,
                finalBalance: remainingBalance,
                isValid: false,
                error: 'Monthly payment too low to cover interest'
            };
        }

        totalInterestPaid += interestPayment;
        remainingBalance -= principalPayment;
        months++;
    }

    return {
        monthsToPayoff: months,
        totalPayments: months * monthlyPayment,
        totalInterest: totalInterestPaid,
        finalBalance: Math.max(0, remainingBalance),
        isValid: true
    };
};

/**
 * Calculates monthly payment required for a loan given balance, APR, and term
 * @param {number} balance - Loan amount
 * @param {number} apr - Annual percentage rate (as percentage, e.g., 8.5)
 * @param {number} termMonths - Loan term in months
 * @returns {number} - Required monthly payment
 */
export const calculateMonthlyPayment = (balance, apr, termMonths) => {
    if (!balance || balance <= 0 || !termMonths || termMonths <= 0) {
        return 0;
    }

    // If no interest, simple calculation
    if (!apr || apr <= 0) {
        return balance / termMonths;
    }

    // Convert APR to monthly rate (APR is already in decimal format 0-1)
    const monthlyRate = apr / 12;
    
    // Use standard loan payment formula: P = L[c(1 + c)^n]/[(1 + c)^n - 1]
    const monthlyPayment = balance * (monthlyRate * Math.pow(1 + monthlyRate, termMonths)) / 
                          (Math.pow(1 + monthlyRate, termMonths) - 1);

    return monthlyPayment;
};

/**
 * Compares multiple debt scenarios and returns analysis
 * @param {Array} currentDebts - Array of current debt objects with balance, apr, monthlyPayment
 * @param {Object} newLoan - New loan object with balance, apr, monthlyPayment
 * @returns {Object} - Comparison analysis
 */
export const compareDebtScenarios = (currentDebts, newLoan) => {
    // Calculate current scenario totals
    const currentTotals = currentDebts.reduce((totals, debt) => {
        const payoffData = calculateLoanPayoff(debt.balance, debt.apr, debt.monthlyPayment);
        
        return {
            totalBalance: totals.totalBalance + debt.balance,
            totalMonthlyPayment: totals.totalMonthlyPayment + debt.monthlyPayment,
            totalInterest: totals.totalInterest + payoffData.totalInterest,
            maxMonthsToPayoff: Math.max(totals.maxMonthsToPayoff, payoffData.monthsToPayoff)
        };
    }, {
        totalBalance: 0,
        totalMonthlyPayment: 0,
        totalInterest: 0,
        maxMonthsToPayoff: 0
    });

    // Calculate new loan scenario
    const newLoanPayoff = calculateLoanPayoff(newLoan.balance, newLoan.apr, newLoan.monthlyPayment);

    // Calculate differences
    const balanceDiff = newLoan.balance - currentTotals.totalBalance;
    const monthlyPaymentDiff = newLoan.monthlyPayment - currentTotals.totalMonthlyPayment;
    const interestDiff = newLoanPayoff.totalInterest - currentTotals.totalInterest;
    const timeDiff = newLoanPayoff.monthsToPayoff - currentTotals.maxMonthsToPayoff;

    return {
        current: currentTotals,
        newLoan: {
            ...newLoan,
            ...newLoanPayoff
        },
        differences: {
            balance: balanceDiff,
            monthlyPayment: monthlyPaymentDiff,
            totalInterest: interestDiff,
            timeToPayoff: timeDiff
        },
        savings: {
            interestSavings: -interestDiff, // Negative diff means savings
            monthlyPaymentSavings: -monthlyPaymentDiff,
            timeSavings: -timeDiff // Negative diff means faster payoff
        }
    };
};

/**
 * Validates refinance calculation inputs
 * @param {Array} selectedAccounts - Selected accounts for refinance
 * @param {Object} newLoanData - New loan parameters
 * @returns {Object} - Validation result with isValid and errors
 */
export const validateRefinanceInputs = (selectedAccounts, newLoanData) => {
    const errors = [];

    // Check selected accounts
    if (!selectedAccounts || selectedAccounts.length === 0) {
        errors.push('Please select at least one account to refinance');
    }

    // Validate selected accounts have required data
    selectedAccounts.forEach((account, index) => {
        if (!account.balance || account.balance <= 0) {
            errors.push(`Account "${account.name}" has invalid balance`);
        }
        if (!account.monthly_payment || account.monthly_payment <= 0) {
            errors.push(`Account "${account.name}" has invalid monthly payment`);
        }
    });

    // Validate new loan data
    if (!newLoanData.balance || parseFloat(newLoanData.balance) <= 0) {
        errors.push('New loan balance must be greater than 0');
    }

    if (!newLoanData.apr || parseFloat(newLoanData.apr) < 0 || parseFloat(newLoanData.apr) > 50) {
        errors.push('New loan APR must be between 0 and 50%');
    }

    if (!newLoanData.monthlyPayment || parseFloat(newLoanData.monthlyPayment) <= 0) {
        errors.push('New loan monthly payment must be greater than 0');
    }

    // Check if monthly payment can cover interest
    const monthlyRate = (parseFloat(newLoanData.apr) / 100) / 12;
    const minimumPayment = parseFloat(newLoanData.balance) * monthlyRate;
    
    if (parseFloat(newLoanData.monthlyPayment) <= minimumPayment) {
        errors.push('Monthly payment must be higher than the minimum interest payment to pay off the loan');
    }

    return {
        isValid: errors.length === 0,
        errors
    };
};

/**
 * Formats time duration in a human-readable format
 * @param {number} months - Duration in months
 * @returns {string} - Formatted duration
 */
export const formatDuration = (months) => {
    if (!months || months <= 0) return '0 months';
    
    if (months === Infinity) return 'Never (payment too low)';
    
    const years = Math.floor(months / 12);
    const remainingMonths = months % 12;
    
    if (years === 0) {
        return `${months} month${months === 1 ? '' : 's'}`;
    }
    
    if (remainingMonths === 0) {
        return `${years} year${years === 1 ? '' : 's'}`;
    }
    
    return `${years} year${years === 1 ? '' : 's'}, ${remainingMonths} month${remainingMonths === 1 ? '' : 's'}`;
};

/**
 * Calculates the breakeven point for a refinance (when savings exceed costs)
 * @param {number} currentMonthlyPayment - Current total monthly payments
 * @param {number} newMonthlyPayment - New loan monthly payment
 * @param {number} refinanceCosts - Upfront costs of refinancing (fees, etc.)
 * @returns {number} - Months to break even, or null if no breakeven
 */
export const calculateBreakevenMonths = (currentMonthlyPayment, newMonthlyPayment, refinanceCosts = 0) => {
    const monthlySavings = currentMonthlyPayment - newMonthlyPayment;
    
    if (monthlySavings <= 0) {
        return null; // No monthly savings, so no breakeven
    }
    
    return Math.ceil(refinanceCosts / monthlySavings);
};